import React, { useEffect, useState } from 'react';
import { BlobServiceClient } from '@azure/storage-blob';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MecationTable from './MedicationTable';

const AZURE_STORAGE_URL = "https://vivotaltest.blob.core.windows.net";
const CONTAINER_NAME = "vivotal-test-blob";
const AZURE_STORAGE_SAS_TOKEN = "sv=2024-11-04&ss=b&srt=sco&sp=rwdlaciytfx&se=2026-07-30T20:10:00Z&st=2025-07-30T11:55:00Z&spr=https&sig=Qcx2utZv%2FKMMBKCC37wBy%2FyiRxHQdITfTpTN6uIzYdk%3D";

type LabReport = {
  id: number;
  title: string;
  doctor: string;
  specialty: string;
  date: string;
  image: string;
};

export default function HealthLayout() {
  const [activeTab, setActiveTab] = useState<'lab' | 'medications' | 'health-plan'>('lab');
  const [reports, setReports] = useState<LabReport[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [previewReport, setPreviewReport] = useState<LabReport | null>(null);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [newReport, setNewReport] = useState<Omit<LabReport, 'id' | 'image'>>({
    title: '',
    doctor: '',
    specialty: '',
    date: ''
  });

  const [doctors, setDoctors] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    fetch("/json/doctors.json")
      .then(res => res.json())
      .then(data => setDoctors(data))
      .catch(err => console.error("Failed to load doctor list:", err));
  }, []);

  const fetchBlobList = async () => {
    try {
      const listUrl = `${AZURE_STORAGE_URL}/${CONTAINER_NAME}?restype=container&comp=list&${AZURE_STORAGE_SAS_TOKEN}`;
      const res = await fetch(listUrl);
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      const text = await res.text();
      const parser = new DOMParser();
      const xml = parser.parseFromString(text, "application/xml");
      const blobNames = Array.from(xml.getElementsByTagName("Name")).map(el => el.textContent);

      const parsed: LabReport[] = (blobNames || []).map((name, i) => ({
        id: i + 1,
        title: name || '',
        doctor: 'Dr. Austin Smith, MD',
        specialty: 'Cardiology',
        date: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }),
        image: `${AZURE_STORAGE_URL}/${CONTAINER_NAME}/${name}`
      }));

      setReports(parsed);
    } catch (err: any) {
      console.error('Blob fetch error:', err);
      setError(err.message || 'Unknown error');
    }
  };

  useEffect(() => {
    fetchBlobList();
  }, []);

  const handleUpload = async () => {
    if (!file || !newReport.title || !newReport.doctor || !newReport.specialty || !newReport.date) {
      alert('Please fill all fields and select a file.');
      return;
    }

    try {
      const blobServiceClient = new BlobServiceClient(`${AZURE_STORAGE_URL}?${AZURE_STORAGE_SAS_TOKEN}`);
      const containerClient = blobServiceClient.getContainerClient(CONTAINER_NAME);
      const blockBlobClient = containerClient.getBlockBlobClient(file.name);

      await blockBlobClient.uploadBrowserData(file);
      toast.success("Upload successful!");

      setShowUpload(false);
      setNewReport({ title: '', doctor: '', specialty: '', date: '' });
      setFile(null);

      await fetchBlobList();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Upload failed. See console for details.");
    }
  };

  const handleViewBenefits = (planName: string) => {
    setSelectedPlan(planName);
    setShowBenefitsModal(true);
  };

  return (
    <div className="section" style={{ width: "100%" }}>

      <div className="heading" style={{ marginBottom: '16px' }}>Health</div>  

      <div style={{ display: 'flex', borderBottom: '1px solid #ccc', marginBottom: '16px' }}>
        <button onClick={() => setActiveTab('lab')} style={{
          border: 'none',
          background: 'transparent',
          padding: '10px 16px',
          borderBottom: activeTab === 'lab' ? '2px solid #3182ce' : 'none',
          color: activeTab === 'lab' ? '#3182ce' : '#666',
          fontWeight: 500,
          cursor: 'pointer'
        }}>Lab Reports</button>

        <button onClick={() => setActiveTab('medications')} style={{
          border: 'none',
          background: 'transparent',
          padding: '10px 16px',
          borderBottom: activeTab === 'medications' ? '2px solid #3182ce' : 'none',
          color: activeTab === 'medications' ? '#3182ce' : '#666',
          fontWeight: 500,
          cursor: 'pointer'
        }}>Medications</button>

        <button onClick={() => setActiveTab('health-plan')} style={{
          border: 'none',
          background: 'transparent',
          padding: '10px 16px',
          borderBottom: activeTab === 'health-plan' ? '2px solid #3182ce' : 'none',
          color: activeTab === 'health-plan' ? '#3182ce' : '#666',
          fontWeight: 500,
          cursor: 'pointer'
        }}>Health Plan & Benefits</button>

        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => setShowUpload(true)} style={{
            padding: '8px 16px',
            backgroundColor: '#3182ce',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 500
          }}>+ Add Report</button>
        </div>
      </div>

      {showUpload && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', padding: '20px', borderRadius: '8px', width: '400px', boxSizing: 'border-box'
          }}>
            <h3 style={{ marginBottom: '16px' }}>Add New Lab Report</h3>

            <input
              type="text"
              placeholder="Title"
              value={newReport.title}
              onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
              style={commonInputStyle}
            />

            <select
              value={newReport.doctor}
              onChange={(e) => setNewReport({ ...newReport, doctor: e.target.value })}
              style={{ ...commonInputStyle, color: newReport.doctor ? '#000' : '#999' }}
            >
              <option value="">Select Doctor</option>
              {doctors.map((doc) => (
                <option key={doc.id} value={doc.name}>{doc.name}</option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Specialty"
              value={newReport.specialty}
              onChange={(e) => setNewReport({ ...newReport, specialty: e.target.value })}
              style={commonInputStyle}
            />

            <input
              type="text"
              placeholder="Date"
              value={newReport.date}
              onChange={(e) => setNewReport({ ...newReport, date: e.target.value })}
              style={commonInputStyle}
            />

            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              style={commonInputStyle}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setShowUpload(false)}>Cancel</button>
              <button
                onClick={handleUpload}
                style={{
                  background: '#38a169',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px'
                }}
              >Upload</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'lab' && (
        <>
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {reports.length === 0 && <p>No reports found or still loading...</p>}
            {reports.map((report) => (
              <div key={report.id} onClick={() => setPreviewReport(report)} style={{
                width: '250px',
                border: '1px solid #eee',
                borderRadius: '10px',
                padding: '12px',
                background: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                cursor: 'pointer'
              }}>
                {/\.(jpg|jpeg|png|gif)$/i.test(report.image) ? (
                  <img
                    src={report.image}
                    alt={report.title}
                    style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px' }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f0f0f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#666'
                  }}>
                    📄 Preview
                  </div>
                )}

                <h4 style={{ margin: '10px 0 4px', fontSize: '16px', wordWrap: 'break-word' }}>{report.title}</h4>
                <p><strong>Doctor:</strong> {report.doctor}</p>
                <p><strong>Specialty:</strong> {report.specialty}</p>
                <p><strong>Date:</strong> {report.date}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'medications' && (
        <MecationTable />
      )}

      {activeTab === 'health-plan' && (
        <div style={{ padding: '20px' }}>
          <h3 style={{ marginBottom: '24px', color: '#2d3748' }}>Health Plan & Benefits</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '70% 30%', gap: '24px' }}>
            {/* Left Column: Plan Name & Type Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* === Plan Card 1 === */}
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '16px', margin: '0', color: '#2d3748' }}>
                    Plan Name & Type
                  </h4>
                  <p style={{ fontSize: '15px', margin: '4px 0 0', color: '#4a5568' }}>
                    HealthGuard Pro – PPO Plan
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#718096', fontSize: '13px' }}>Plan ID</span>
                  <strong>HB123456789</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#718096', fontSize: '13px' }}>Effective Dates</span>
                  <strong>Jan 1, 2025 - Dec 31, 2025</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <span style={{ color: '#718096', fontSize: '13px' }}>Plan Renewal Date</span>
                  <strong>Jan 1, 2025</strong>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleViewBenefits('HealthGuard Pro – PPO Plan')}
                    style={{
                      flex: 1,
                      backgroundColor: '#3182ce',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    View Full Benefits
                  </button>
                  <button
                    style={{
                      flex: 1,
                      backgroundColor: '#e2e8f0',
                      color: '#2d3748',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Download ID Card
                  </button>
                </div>
              </div>

              {/* === Plan Card 2 === */}
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '16px', margin: '0', color: '#2d3748' }}>
                    Plan Name & Type
                  </h4>
                  <p style={{ fontSize: '15px', margin: '4px 0 0', color: '#4a5568' }}>
                    BlueCross BlueShield – PPO Plan
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#718096', fontSize: '13px' }}>Plan ID</span>
                  <strong>ABC123456789</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#718096', fontSize: '13px' }}>Effective Dates</span>
                  <strong>Jan 1, 2025 - Dec 31, 2025</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <span style={{ color: '#718096', fontSize: '13px' }}>Plan Renewal Date</span>
                  <strong>Jan 1, 2025</strong>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleViewBenefits('BlueCross BlueShield – PPO Plan')}
                    style={{
                      flex: 1,
                      backgroundColor: '#3182ce',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    View Full Benefits
                  </button>
                  <button
                    style={{
                      flex: 1,
                      backgroundColor: '#e2e8f0',
                      color: '#2d3748',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Download ID Card
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column: Primary Care Provider and Financial Progress */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* === Primary Care Provider === */}
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                }}
              >
                <h4 style={{ marginBottom: '10px', color: '#2d3748' }}>Primary Care Provider</h4>
                <p style={{ color: '#4a5568', marginBottom: '4px' }}>
                  <strong>Dr. Emily Chen</strong> – Family Medicine
                </p>
                <p style={{ color: '#4a5568', marginBottom: '2px' }}>
                  📞 (555) 123-4567
                </p>
                <p style={{ color: '#4a5568' }}>
                  📍 123 Health Ave, Wellness City, CA 90210
                </p>
              </div>

              {/* === Financial Progress === */}
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                }}
              >
                <h4 style={{ marginBottom: '16px', color: '#2d3748' }}>Financial Progress</h4>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#4a5568' }}>Deductible Met</span>
                    <span style={{ fontWeight: '500' }}>$1,500 / $3,000</span>
                  </div>
                  <div style={{ background: '#edf2f7', height: '8px', borderRadius: '4px' }}>
                    <div
                      style={{
                        width: '50%',
                        height: '100%',
                        backgroundColor: '#3182ce',
                        borderRadius: '4px',
                      }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#4a5568' }}>Out-of-Pocket Maximum Met</span>
                    <span style={{ fontWeight: '500' }}>$2,000 / $6,000</span>
                  </div>
                  <div style={{ background: '#edf2f7', height: '8px', borderRadius: '4px' }}>
                    <div
                      style={{
                        width: '33%',
                        height: '100%',
                        backgroundColor: '#38a169',
                        borderRadius: '4px',
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {previewReport && (
        <div
          onClick={() => setPreviewReport(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              padding: '16px',
              borderRadius: '8px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <h3>{previewReport.title}</h3>
            {previewReport.image.match(/\.(jpg|jpeg|png|gif)$/i) ? (
              <img
                src={previewReport.image}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '4px' }}
              />
            ) : (
              <iframe
                src={previewReport.image}
                style={{ width: '100%', height: '80vh' }}
                title="File Preview"
              ></iframe>
            )}
            <div style={{ textAlign: 'right', marginTop: '8px' }}>
              <button onClick={() => setPreviewReport(null)} style={{ padding: '6px 12px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showBenefitsModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowBenefitsModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: '0', color: '#2d3748', fontSize: '20px' }}>Full Benefits</h3>
              <button
                onClick={() => setShowBenefitsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#718096',
                  padding: '0'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#3182ce', fontSize: '18px' }}>Plan Name</h4>
              <p style={{ margin: '0', fontSize: '16px', fontWeight: '500', color: '#2d3748' }}>{selectedPlan}</p>
            </div>

            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
              <h5 style={{ margin: '0 0 12px 0', color: '#2d3748' }}>Benefits Overview</h5>
              <ul style={{ margin: '0', paddingLeft: '20px', color: '#4a5568' }}>
                <li>Preventive Care: 100% covered</li>
                <li>Emergency Services: 80% covered</li>
                <li>Prescription Drugs: Tiered coverage</li>
                <li>Mental Health: 70% covered</li>
                <li>Hospitalization: 90% covered</li>
                <li>Specialist Visits: 85% covered</li>
              </ul>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowBenefitsModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e2e8f0',
                  color: '#2d3748',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

const commonInputStyle: React.CSSProperties = {
  marginBottom: '10px',
  width: '100%',
  padding: '8px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  fontSize: '14px',
  lineHeight: '1.5',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  height: '40px',
};
